import { JsmClientError } from '../errors';
import { JSM_BASE_URL } from '../settings';
import {
  type FieldOption,
  type FieldSettings,
  type FieldValue,
  type FormSettings,
  type JsmErrorResponseDto,
  type JsmWidgetDto,
  type JsmWidgetFormDto,
  type JsmWidgetFormFieldDefaultValueDto,
  type JsmWidgetFormFieldDto,
  JsmWidgetFormFieldTypeMap,
  type JsmWidgetSearchResponseDto,
} from '../types';
import { JsmClient } from '../utilities';
import { mapJsmErrorResponseDtoToFormError } from './mapJsmErrorResponseDtoToFormError';

export const loadJsmWidgetSettings = async (dataKey: string): Promise<FormSettings> => {
  const jsmClient = new JsmClient(JSM_BASE_URL);
  const url = `${encodeURIComponent(dataKey)}/widget`;

  try {
    const response = await jsmClient.get<JsmWidgetDto>(url);
    console.debug('loadJsmWidgetSettings: JSM-widget response:', response);
    return mapJsmWidgetDtoToFormSettings(response);
  } catch (error: any) {
    console.error('loadJsmWidgetSettings: JSM error:', JSON.parse(JSON.stringify(error)));
    const jsmError = error.responseBody as JsmErrorResponseDto;
    throw mapJsmErrorResponseDtoToFormError(jsmError);
  }
};

export const loadJsmWidgetSettingsByRequestType = async (
  dataKey: string,
  requestTypeId: string,
): Promise<FormSettings> => {
  const jsmClient = new JsmClient(JSM_BASE_URL);
  const url = `${encodeURIComponent(dataKey)}/search?query=&limit=100`;

  try {
    const response = await jsmClient.get<JsmWidgetSearchResponseDto>(url);
    console.debug('loadJsmWidgetSettingsByRequestType: JSM-widget response:', response);
    const forms = response.requestTypes.filter((form: JsmWidgetFormDto) => form.id === requestTypeId);
    if (forms.length === 0) {
      throw new JsmClientError(400, {
        friendlyReason: {
          title: 'Configuration error',
          body: `Cannot load form for request type of Id: ${requestTypeId}`,
        },
      });
    }
    return mapJsmWidgetFormDtoToFormSettings(forms[0]);
  } catch (error: any) {
    console.error('loadJsmWidgetSettingsByRequestType: JSM error:', JSON.parse(JSON.stringify(error)));
    const jsmError = error.responseBody as JsmErrorResponseDto;
    throw mapJsmErrorResponseDtoToFormError(jsmError);
  }
};

const mapJsmWidgetDtoToFormSettings = (widget: JsmWidgetDto): FormSettings => {
  const formSettings = mapJsmWidgetFormDtoToFormSettings(widget.form);
  // formSettings.config.requestTypeId = widget.configuration.requestTypeId;
  formSettings.config.confirmationText = widget.configuration.confirmationText;
  return formSettings;
};

const mapJsmWidgetFormDtoToFormSettings = (form: JsmWidgetFormDto): FormSettings => {
  const fieldSettings = form.fields.map((field: JsmWidgetFormFieldDto, key: any): FieldSettings => {
    return {
      key,
      type: getFieldType(field),
      name: field.id,
      label: field.name,
      description: field.description,
      defaultValue: getFieldDefaultValue(field),
      required: field.required,
      options: getSelectOptions(field),
    };
  });

  return {
    name: form.name,
    description: form.description,
    fields: fieldSettings,
    config: {
      requestTypeId: form.id,
    },
  };
};

const getFieldType = (field: JsmWidgetFormFieldDto): string => {
  return JsmWidgetFormFieldTypeMap[field.type] !== undefined ? JsmWidgetFormFieldTypeMap[field.type] : field.type;
};

const getFieldDefaultValue = (field: JsmWidgetFormFieldDto): FieldValue => {
  if (field.defaultValues && field.defaultValues.length > 0) {
    const defaultOption: JsmWidgetFormFieldDefaultValueDto = field.defaultValues[0];
    return defaultOption.value;
  }
  return undefined;
};

const getSelectOptions = (field: JsmWidgetFormFieldDto): FieldOption[] | null => {
  if (field.possibleValues && field.possibleValues.length > 0) {
    const res = [];
    for (const option of field.possibleValues) {
      res.push({ ...option });
    }
    return res;
  }
  return null;
};
